import "./EventListTable.scss";
import axios from "axios";
import ChannelSelection from "@/components/Events/ChannelSelection/ChannelSelection.vue";
import EventTableActionMenu from "@/components/Events/EventTableActionMenu/EventTableActionMenu.vue";
import EventSuggestionDialog from "@/components/Events/EventListTable/EventSuggestionDialog/EventSuggestionDialog.vue";
import CategorySetDialog from "@/components/Events/EventListTable/CategorySetDialog/CategorySetDialog.vue";
import FeatureSetDialog from "@/components/Events/EventListTable/FeatureSetDialog/FeatureSetDialog.vue";
import ColumnVisibilitySelector from "@/components/Common/ColumnVisibilitySelector/ColumnVisibilitySelector.vue";
import {
  getTokenHeader,
  getPrincipalName,
  getApiKeyDescription,
  getThumbnailUrl,
  getObjectDiff
} from "@/helpers/commonHelper";
import { imports, stringifyUTF8 } from "@/helpers/eventHelper";
import {
  buildSuggestionPromptPayload,
  executeSuggestionPrompt,
  getCurrentUserIdFromSession
} from "@/helpers/suggestionHelper";
import {
  countSuggestionItems,
  extractResolvedLabels,
  formatNumberDe,
  getAppliedStateForItemByKey,
  resolveLabelFromValue,
  getNormalizedIdList,
  normalizeEntityId,
  getSuggestionErrorForItemByKey,
  getSuggestionMapKey,
  getSuggestionsForItemByKey,
  isItemLoadingSuggestionsByKey,
  isValidBatchMode,
  normalizeLabel,
  removePendingEntry,
  runBulkTagUpdateRequest,
  syncSelectionWithLatestItems,
  togglePendingEntry
} from "@/helpers/batchDialogHelper";
const env = require(`@/environments/${process.env.VUE_APP_ENV_PATH}`);

const CATEGORY_SUGGESTION_PROMPT_SHORT_ID = "sBDHKQgbT";
const FEATURE_SUGGESTION_PROMPT_SHORT_ID = "sBDHKQgbT";

export default {
  name: "EventListTable",
  components: {
    ChannelSelection,
    EventTableActionMenu,
    EventSuggestionDialog,
    CategorySetDialog,
    FeatureSetDialog,
    ColumnVisibilitySelector
  },
  data: () => ({
    page: 1,
    pageCount: 0,
    itemsPerPage: 10,
    selected: [],
    allPagesSelected: false,
    dialogItemsLoaded: [],
    categoryDialogOpen: false,
    currentSuggestionType: "category",
    suggestingCategories: false,
    suggestingIndex: -1,
    suggestingItemId: null,
    suggestedCategoriesById: {},
    suggestionErrorsById: {},
    abortSuggestingCategories: false,
    categoriesForPrompt: [],
    featuresForPrompt: [],
    suggestionsCompleted: false,
    applyingCategories: false,
    appliedSuggestionsById: {},
    applyCompleted: false,
    pendingSaveById: {},
    categorySetDialogOpen: false,
    categorySetMode: "add",
    categorySetQuery: "",
    categorySetPending: [],
    categorySetItemsLoaded: [],
    categorySetSaving: false,
    categorySetConfirmOpen: false,
    categorySetStatsLoading: false,
    categorySetStatsError: "",
    categorySetStatsByTag: {},
    categorySetTotal: 0,
    categorySetCustomTags: [],
    categorySetSaveError: "",
    categorySetSelection: null,
    categorySetIsAllPages: false,
    categorySetJobState: null,
    categorySetJobPollTimer: null,
    featureSetDialogOpen: false,
    featureSetMode: "add",
    featureSetQuery: "",
    featureSetPending: [],
    featureSetItemsLoaded: [],
    featureSetSaving: false,
    featureSetConfirmOpen: false,
    featureSetStatsLoading: false,
    featureSetStatsError: "",
    featureSetStatsByTag: {},
    featureSetTotal: 0,
    featureSetSaveError: "",
    featureSetSelection: null,
    featureSetIsAllPages: false
  }),
  props: [
    "config",
    "headers",
    "visibleColumnKeys",
    "columnOptions",
    "columnSettingsStorageKey",
    "columnContextKey",
    "events",
    "copyEvent",
    "editEvent",
    "previewEvent",
    "deleteEvent",
    "eventCount",
    "getTypeIcon",
    "getStartTime",
    "getOrganizer",
    "getLocation",
    "getLocationCity",
    "filter",
    "loadingExport",
    "exportType",
    "isLoading",
    "getRegionality",
    "categories",
    "features",
    "featureGroups"
  ],
  methods: {
    nextPage() {
      const totalPages = Math.ceil(this.eventCount / this.itemsPerPage);
      if (this.page < totalPages) this.page++;
    },
    previousPage() {
      if (this.page > 1) this.page--;
    },
    triggerFilter() {
      const { page } = this.tableOptions;
      this.$emit("setFilterPage", page);
    },
    getPrincipalName,
    getApiKeyDescription,
    getThumbnailUrl,
    previewImage(item) {
      const url = item?.pictures[0]?.url;
      return this.getThumbnailUrl(url, "events", 70, "-");
    },
    morePictures(item) {
      return item.pictures.length > 1;
    },
    getTitle(item) {
      if (item.moment?.title?.length) {
        return item.moment.title;
      }
      return item.title;
    },
    updateItemsPerPage(value) {
      this.clearSelection();
      this.$emit("setFilterLimit", value);
    },
    selectAllPages() {
      this.allPagesSelected = true;
    },
    clearSelection() {
      this.selected = [];
      this.allPagesSelected = false;
    },
    getDialogSelectionItems() {
      if (this.selected.length) {
        const latestById = new Map(
          this.uniqueEvents.map(item => [this.getItemStateKey(item), item])
        );
        return this.selected
          .map(item => {
            const id = this.getItemStateKey(item);
            return latestById.get(id) || item;
          })
          .filter(Boolean);
      }
      if (this.allPagesSelected) {
        return this.uniqueEvents;
      }
      return [];
    },
    syncSelectedWithLatestEvents(selectedIds = []) {
      if (!selectedIds.length) return;
      this.selected = syncSelectionWithLatestItems(
        selectedIds,
        this.uniqueEvents,
        item => this.getItemStateKey(item)
      );
    },
    normalizeEntityId(value) {
      return normalizeEntityId(value);
    },
    getItemStateKey(itemOrId) {
      if (!itemOrId) return "";
      if (typeof itemOrId === "object") {
        return this.normalizeEntityId(
          itemOrId?._id || itemOrId?.id || itemOrId
        );
      }
      return this.normalizeEntityId(itemOrId);
    },
    getItemSuggestionStateKey(item, index) {
      return this.getItemStateKey(item) || `__index_${index}`;
    },
    getSuggestionMapKey(itemOrId, suggestionType = this.currentSuggestionType) {
      return getSuggestionMapKey(itemOrId, suggestionType, value =>
        typeof value === "object"
          ? this.getItemStateKey(value)
          : this.normalizeEntityId(value)
      );
    },
    getSuggestionsForItem(item, suggestionType = this.currentSuggestionType) {
      const key = this.getSuggestionMapKey(item, suggestionType);
      return getSuggestionsForItemByKey(this.suggestedCategoriesById, key);
    },
    getSuggestionErrorForItem(
      item,
      suggestionType = this.currentSuggestionType
    ) {
      const key = this.getSuggestionMapKey(item, suggestionType);
      return getSuggestionErrorForItemByKey(this.suggestionErrorsById, key);
    },
    getAppliedStateForItem(item, suggestionType = this.currentSuggestionType) {
      const key = this.getSuggestionMapKey(item, suggestionType);
      return getAppliedStateForItemByKey(this.appliedSuggestionsById, key);
    },
    isItemLoadingSuggestions(
      item,
      suggestionType = this.currentSuggestionType
    ) {
      const key = this.getSuggestionMapKey(item, suggestionType);
      return isItemLoadingSuggestionsByKey(this.suggestingItemId, key);
    },
    getTabSuggestionCount(suggestionType) {
      return countSuggestionItems(
        this.dialogItems,
        suggestionType,
        this.getSuggestionsForItem
      );
    },
    setSuggestionType(suggestionType) {
      if (!["category", "feature"].includes(suggestionType)) return;
      if (this.currentSuggestionType === suggestionType) return;
      this.currentSuggestionType = suggestionType;
      this.suggestingItemId = null;
    },
    logSuggestionFlow(step, meta = {}) {
      const timestamp = new Date().toISOString();
      console.log(`[SuggestionFlow][${timestamp}] ${step}`, meta);
    },
    async openSuggestionDialog(type) {
      this.logSuggestionFlow("DIALOG_OPEN_START", {
        type,
        selectedCount: this.selected.length,
        allPagesSelected: this.allPagesSelected
      });
      this.currentSuggestionType = type;
      this.suggestedCategoriesById = {};
      this.suggestionErrorsById = {};
      this.suggestionsCompleted = false;
      this.applyingCategories = false;
      this.appliedSuggestionsById = {};
      this.applyCompleted = false;
      this.pendingSaveById = {};
      this.dialogItemsLoaded = this.getDialogSelectionItems().map(item => ({
        ...item
      }));
      this.categoryDialogOpen = true;
      this._debugLogged = false; // Reset debug flag

      // Lade alle verfügbaren Kategorien/Features für korrekte Anzeige
      await this.loadAvailableItemsForDisplay();

      // Nur Detaildaten nachladen; Vorschläge werden explizit pro Zeile erzeugt.
      await this.enrichDialogItemsWithDetails();
      this.logSuggestionFlow("DIALOG_OPEN_READY", {
        type,
        dialogItemsCount: this.dialogItems.length
      });
    },
    getMainCategoryLabel(item) {
      if (!item) return "";
      return this.getCategoryTitle(item.category || item.category_id || null);
    },
    async openCategoryDialog() {
      const selectionItems = this.getDialogSelectionItems().map(item => ({
        ...item
      }));
      const selectionIds = selectionItems
        .map(item => this.getItemStateKey(item))
        .filter(Boolean);

      this.categorySetMode = "add";
      this.categorySetQuery = "";
      this.categorySetPending = [];
      this.categorySetConfirmOpen = false;
      this.categorySetSaveError = "";
      this.categorySetStatsError = "";
      this.categorySetItemsLoaded = selectionItems;
      this.categorySetSelection = this.allPagesSelected
        ? { filter: { ...this.filter } }
        : { ids: selectionIds };
      this.categorySetIsAllPages = this.allPagesSelected;
      this.categorySetTotal = this.categorySetIsAllPages
        ? Number(this.eventCount) || 0
        : selectionIds.length;
      this.categorySetDialogOpen = true;

      await this.loadCategoriesForPrompt();
      await this.syncCategorySetItemsWithLatestDetails();
    },
    async syncCategorySetItemsWithLatestDetails() {
      const items = Array.isArray(this.categorySetItemsLoaded)
        ? this.categorySetItemsLoaded
        : [];
      if (!items.length) return;

      try {
        const eventIds = this.getNormalizedIdList(
          items.map(item => this.getItemStateKey(item)).filter(Boolean)
        );
        const byId = await this.bulkReadEventsByIds(eventIds);
        this.categorySetItemsLoaded = items.map(item => {
          const itemId = this.getItemStateKey(item);
          return {
            ...item,
            ...(byId[itemId] || {})
          };
        });
        return;
      } catch (error) {
        // Fallback auf Einzel-Read, falls bulk-read temporär nicht verfügbar ist.
      }

      const refreshedItems = [];
      for (let index = 0; index < items.length; index++) {
        const item = items[index];
        try {
          const fullEvent = await this.getFullEventForSuggestion(
            item,
            "CATEGORY_SET_DIALOG_OPEN"
          );
          refreshedItems.push({
            ...item,
            ...(fullEvent || {})
          });
        } catch (error) {
          refreshedItems.push(item);
        }
      }

      this.categorySetItemsLoaded = refreshedItems;
    },
    async openFeatureDialog() {
      const selectionItems = this.getDialogSelectionItems().map(item => ({
        ...item
      }));
      const selectionIds = selectionItems
        .map(item => this.getItemStateKey(item))
        .filter(Boolean);

      this.featureSetMode = "add";
      this.featureSetQuery = "";
      this.featureSetPending = [];
      this.featureSetConfirmOpen = false;
      this.featureSetSaveError = "";
      this.featureSetStatsError = "";
      this.featureSetItemsLoaded = selectionItems;
      this.featureSetSelection = this.allPagesSelected
        ? { filter: { ...this.filter } }
        : { ids: selectionIds };
      this.featureSetIsAllPages = this.allPagesSelected;
      this.featureSetTotal = this.featureSetIsAllPages
        ? Number(this.eventCount) || 0
        : selectionIds.length;
      this.featureSetDialogOpen = true;

      await this.loadFeaturesForPrompt();
      await this.syncFeatureSetItemsWithLatestDetails();
    },
    async syncFeatureSetItemsWithLatestDetails() {
      const items = Array.isArray(this.featureSetItemsLoaded)
        ? this.featureSetItemsLoaded
        : [];
      if (!items.length) return;

      try {
        const eventIds = this.getNormalizedIdList(
          items.map(item => this.getItemStateKey(item)).filter(Boolean)
        );
        const byId = await this.bulkReadEventsByIds(eventIds);
        this.featureSetItemsLoaded = items.map(item => {
          const itemId = this.getItemStateKey(item);
          return {
            ...item,
            ...(byId[itemId] || {})
          };
        });
        return;
      } catch (error) {
        // Fallback auf Einzel-Read, falls bulk-read temporär nicht verfügbar ist.
      }

      const refreshedItems = [];
      for (let index = 0; index < items.length; index++) {
        const item = items[index];
        try {
          const fullEvent = await this.getFullEventForSuggestion(
            item,
            "FEATURE_SET_DIALOG_OPEN"
          );
          refreshedItems.push({
            ...item,
            ...(fullEvent || {})
          });
        } catch (error) {
          refreshedItems.push(item);
        }
      }
      this.featureSetItemsLoaded = refreshedItems;
    },
    async openSuggestionReviewDialog() {
      await this.openSuggestionDialog("category");
    },
    closeCategorySetDialog() {
      this.categorySetDialogOpen = false;
      this.categorySetPending = [];
      this.categorySetQuery = "";
      this.categorySetItemsLoaded = [];
      this.categorySetConfirmOpen = false;
      this.categorySetStatsError = "";
      this.categorySetSaveError = "";
      this.categorySetSelection = null;
      this.categorySetIsAllPages = false;
      this.categorySetStatsByTag = {};
      this.categorySetCustomTags = [];
      this.categorySetTotal = 0;
    },
    setCategorySetMode(mode) {
      if (!isValidBatchMode(mode)) return;
      this.categorySetMode = mode;
      this.categorySetQuery = "";
    },
    setCategorySetQuery(query) {
      this.categorySetQuery = `${query || ""}`;
    },
    closeFeatureSetDialog() {
      this.featureSetDialogOpen = false;
      this.featureSetPending = [];
      this.featureSetQuery = "";
      this.featureSetItemsLoaded = [];
      this.featureSetConfirmOpen = false;
      this.featureSetStatsError = "";
      this.featureSetSaveError = "";
      this.featureSetSelection = null;
      this.featureSetIsAllPages = false;
      this.featureSetStatsByTag = {};
      this.featureSetTotal = 0;
    },
    setFeatureSetMode(mode) {
      if (!isValidBatchMode(mode)) return;
      this.featureSetMode = mode;
      this.featureSetQuery = "";
    },
    setFeatureSetQuery(query) {
      this.featureSetQuery = `${query || ""}`;
    },
    formatNumberDe(value) {
      return formatNumberDe(value);
    },
    getCategorySetStats(tag) {
      const normalizedTag = `${tag || ""}`.trim();
      const total = Number(this.categorySetTotal) || 0;
      const base =
        this.findCategoryStatByTag(this.categorySetStatsByTag, normalizedTag) ||
        {};
      const fallback =
        this.findCategoryStatByTag(
          this.categorySetFallbackStatsByTag,
          normalizedTag
        ) || {};
      const has = Math.max(Number(base.has) || 0, Number(fallback.has) || 0);
      const main = Math.max(Number(base.main) || 0, Number(fallback.main) || 0);
      return {
        total,
        has,
        main,
        neu: Math.max(total - has, 0)
      };
    },
    isCategoryTagVisible(tag) {
      const stats = this.getCategorySetStats(tag);
      if (this.categorySetMode === "remove") {
        return stats.has > 0;
      }
      if (this.categorySetIsAllPages) {
        return true;
      }
      if (stats.has < stats.total) {
        return true;
      }
      // Hauptkategorien sollen im Add-Modus trotzdem anwählbar bleiben,
      // damit sie zusätzlich in additional_categories gesetzt werden können.
      return stats.main > 0;
    },
    findCategoryStatByTag(statsMap, tag) {
      if (!statsMap || !tag) return null;
      if (statsMap[tag]) return statsMap[tag];
      const normalizedTag = this.normalizeCategoryLabel(tag);
      const matchingKey = Object.keys(statsMap).find(
        key => this.normalizeCategoryLabel(key) === normalizedTag
      );
      return matchingKey ? statsMap[matchingKey] : null;
    },
    resolveCategoryTag(value) {
      if (!value) return "";

      if (typeof value === "object") {
        const nested =
          value?.category ||
          value?.categoryObj ||
          value?.item ||
          value?.value ||
          null;
        if (nested && nested !== value) {
          const nestedTag = this.resolveCategoryTag(nested);
          if (nestedTag) return nestedTag;
        }
        return `${value?.title ||
          value?.name ||
          value?.short_title ||
          value?.label ||
          ""}`.trim();
      }

      const asText = `${value}`.trim();
      if (!asText) return "";

      const mappedTitle = this.getCategoryTitle(value);
      if (mappedTitle) return `${mappedTitle}`.trim();

      // Falls das Backend bereits Klartext-Kategorien liefert.
      if (typeof value === "string") return asText;
      return "";
    },
    findFeatureStatByTag(statsMap, tag) {
      if (!statsMap || !tag) return null;
      if (statsMap[tag]) return statsMap[tag];
      const normalizedTag = this.normalizeCategoryLabel(tag);
      const matchingKey = Object.keys(statsMap).find(
        key => this.normalizeCategoryLabel(key) === normalizedTag
      );
      return matchingKey ? statsMap[matchingKey] : null;
    },
    getFeatureSetStats(tag) {
      const normalizedTag = `${tag || ""}`.trim();
      const total = Number(this.featureSetTotal) || 0;
      const base =
        this.findFeatureStatByTag(this.featureSetStatsByTag, normalizedTag) ||
        {};
      const fallback =
        this.findFeatureStatByTag(
          this.featureSetFallbackStatsByTag,
          normalizedTag
        ) || {};
      const has = Math.max(Number(base.has) || 0, Number(fallback.has) || 0);
      return {
        total,
        has,
        neu: Math.max(total - has, 0)
      };
    },
    isFeatureTagVisible(tag) {
      const stats = this.getFeatureSetStats(tag);
      if (this.featureSetMode === "remove") {
        return this.featureSetIsAllPages ? true : stats.has > 0;
      }
      if (this.featureSetIsAllPages) {
        return true;
      }
      return stats.has < stats.total;
    },
    resolveFeatureTag(value) {
      if (!value) return "";

      if (typeof value === "object") {
        const nested = value?.feature || value?.item || value?.value || null;
        if (nested && nested !== value) {
          const nestedTag = this.resolveFeatureTag(nested);
          if (nestedTag) return nestedTag;
        }
        return `${value?.title || value?.name || value?.label || ""}`.trim();
      }

      const asText = `${value}`.trim();
      if (!asText) return "";

      const mappedTitle = this.getFeatureTitle(value);
      if (mappedTitle) return `${mappedTitle}`.trim();

      if (typeof value === "string") return asText;
      return "";
    },
    resolveFeatureSetTagId(tag) {
      const normalizedTag = this.normalizeCategoryLabel(tag);
      return this.featureSetTagIdByNormalizedTag[normalizedTag] || "";
    },
    resolveCategorySetTagId(tag) {
      const normalizedTag = this.normalizeCategoryLabel(tag);
      return this.categorySetTagIdByNormalizedTag[normalizedTag] || "";
    },
    getNextAdditionalCategoryIds(item, addTagIds = [], removeTagIds = []) {
      const currentIds = (Array.isArray(item?.additional_categories)
        ? item.additional_categories
        : []
      )
        .map(value => this.normalizeEntityId(value))
        .filter(Boolean);

      let nextIds = currentIds.filter(id => !removeTagIds.includes(id));
      addTagIds.forEach(id => {
        if (!nextIds.includes(id)) {
          nextIds.push(id);
        }
      });
      return nextIds;
    },
    applyCategorySetUpdatesToItems(
      items,
      eventIdSet,
      addTagIds = [],
      removeTagIds = []
    ) {
      if (!Array.isArray(items) || !eventIdSet?.size) return;

      items.forEach(item => {
        const eventId = this.getItemStateKey(item);
        if (!eventIdSet.has(eventId)) return;

        const nextAdditionalCategoryIds = this.getNextAdditionalCategoryIds(
          item,
          addTagIds,
          removeTagIds
        );
        this.$set(item, "additional_categories", nextAdditionalCategoryIds);

        if (Array.isArray(item?.categories)) {
          const mainCategoryId = this.normalizeEntityId(
            item?.category || item?.category_id
          );
          const mergedCategoryIds = [
            ...new Set([
              ...nextAdditionalCategoryIds,
              ...(mainCategoryId ? [mainCategoryId] : [])
            ])
          ];
          this.$set(item, "categories", mergedCategoryIds);
        }
      });
    },
    applyCategorySetLocalUpdates(
      eventIds = [],
      addTagIds = [],
      removeTagIds = []
    ) {
      const eventIdSet = new Set((eventIds || []).map(id => `${id}`));
      if (!eventIdSet.size) return;

      this.applyCategorySetUpdatesToItems(
        this.events,
        eventIdSet,
        addTagIds,
        removeTagIds
      );
      this.applyCategorySetUpdatesToItems(
        this.categorySetItemsLoaded,
        eventIdSet,
        addTagIds,
        removeTagIds
      );
      this.applyCategorySetUpdatesToItems(
        this.selected,
        eventIdSet,
        addTagIds,
        removeTagIds
      );
      this.applyCategorySetUpdatesToItems(
        this.dialogItemsLoaded,
        eventIdSet,
        addTagIds,
        removeTagIds
      );
    },
    getNextFeatureIds(item, addTagIds = [], removeTagIds = []) {
      const currentIds = (Array.isArray(item?.features) ? item.features : [])
        .map(value => this.normalizeEntityId(value))
        .filter(Boolean);

      let nextIds = currentIds.filter(id => !removeTagIds.includes(id));
      addTagIds.forEach(id => {
        if (!nextIds.includes(id)) {
          nextIds.push(id);
        }
      });
      return nextIds;
    },
    applyFeatureSetUpdatesToItems(
      items,
      eventIdSet,
      addTagIds = [],
      removeTagIds = []
    ) {
      if (!Array.isArray(items) || !eventIdSet?.size) return;

      items.forEach(item => {
        const eventId = this.getItemStateKey(item);
        if (!eventIdSet.has(eventId)) return;
        const nextFeatureIds = this.getNextFeatureIds(
          item,
          addTagIds,
          removeTagIds
        );
        this.$set(item, "features", nextFeatureIds);
      });
    },
    applyFeatureSetLocalUpdates(
      eventIds = [],
      addTagIds = [],
      removeTagIds = []
    ) {
      const eventIdSet = new Set((eventIds || []).map(id => `${id}`));
      if (!eventIdSet.size) return;

      this.applyFeatureSetUpdatesToItems(
        this.events,
        eventIdSet,
        addTagIds,
        removeTagIds
      );
      this.applyFeatureSetUpdatesToItems(
        this.featureSetItemsLoaded,
        eventIdSet,
        addTagIds,
        removeTagIds
      );
      this.applyFeatureSetUpdatesToItems(
        this.selected,
        eventIdSet,
        addTagIds,
        removeTagIds
      );
      this.applyFeatureSetUpdatesToItems(
        this.dialogItemsLoaded,
        eventIdSet,
        addTagIds,
        removeTagIds
      );
    },
    toggleCategorySetEntry(entry) {
      this.categorySetPending = togglePendingEntry(this.categorySetPending, {
        tag: entry?.tag || entry?.title || "",
        type: this.categorySetMode
      });
      this.categorySetSaveError = "";
    },
    removeCategorySetPending(payload) {
      this.categorySetPending = removePendingEntry(
        this.categorySetPending,
        payload?.tag || payload || ""
      );
    },
    openCategorySetConfirm() {
      if (!this.categorySetCanSave || this.categorySetSaving) return;
      this.categorySetConfirmOpen = true;
      this.categorySetSaveError = "";
    },
    closeCategorySetConfirm() {
      this.categorySetConfirmOpen = false;
    },
    toggleFeatureSetEntry(entry) {
      this.featureSetPending = togglePendingEntry(this.featureSetPending, {
        tag: entry?.tag || entry?.title || "",
        type: this.featureSetMode
      });
      this.featureSetSaveError = "";
    },
    removeFeatureSetPending(payload) {
      this.featureSetPending = removePendingEntry(
        this.featureSetPending,
        payload?.tag || payload || ""
      );
    },
    openFeatureSetConfirm() {
      if (!this.featureSetCanSave || this.featureSetSaving) return;
      this.featureSetConfirmOpen = true;
      this.featureSetSaveError = "";
    },
    closeFeatureSetConfirm() {
      this.featureSetConfirmOpen = false;
    },
    closeCategorySetJobCard() {
      if (!this.categorySetJobState?.running) {
        this.categorySetJobState = null;
      }
    },
    clearCategorySetJobTimer() {
      if (this.categorySetJobPollTimer) {
        clearInterval(this.categorySetJobPollTimer);
        this.categorySetJobPollTimer = null;
      }
    },
    async saveCategorySetChanges() {
      if (this.categorySetSaving) return;

      const eventIds = this.getNormalizedIdList(
        this.categorySetSelection?.ids?.length
          ? this.categorySetSelection.ids
          : this.categorySetItemsLoaded
              .map(item => this.getItemStateKey(item))
              .filter(Boolean)
      );

      if (!eventIds.length) {
        this.$store.dispatch("createError", {
          text: "Keine Änderungen zum Speichern vorhanden."
        });
        return;
      }

      this.categorySetSaving = true;
      this.categorySetSaveError = "";

      const add = this.categorySetPending
        .filter(change => change.type === "add")
        .map(change => change.tag)
        .filter(Boolean);
      const remove = this.categorySetPending
        .filter(change => change.type === "remove")
        .map(change => change.tag)
        .filter(Boolean);

      const addTagIds = [
        ...new Set(
          add.map(tag => this.resolveCategorySetTagId(tag)).filter(Boolean)
        )
      ];
      const removeTagIds = [
        ...new Set(
          remove.map(tag => this.resolveCategorySetTagId(tag)).filter(Boolean)
        )
      ];
      const unresolvedTags = [
        ...add.filter(tag => !this.resolveCategorySetTagId(tag)),
        ...remove.filter(tag => !this.resolveCategorySetTagId(tag))
      ];

      if (!addTagIds.length && !removeTagIds.length) {
        this.$store.dispatch("createError", {
          text: "Keine gültigen Kategorien zum Speichern gefunden."
        });
        this.categorySetSaving = false;
        return;
      }

      if (unresolvedTags.length) {
        this.$store.dispatch("createError", {
          text: `Einige Kategorien konnten nicht zugeordnet werden: ${[
            ...new Set(unresolvedTags)
          ].join(", ")}`
        });
      }

      const requests = [
        addTagIds.length
          ? this.runBulkTagUpdate({
              ids: eventIds,
              field: "additional_categories",
              tagIds: addTagIds,
              action: "add"
            })
          : null,
        removeTagIds.length
          ? this.runBulkTagUpdate({
              ids: eventIds,
              field: "additional_categories",
              tagIds: removeTagIds,
              action: "remove"
            })
          : null
      ].filter(Boolean);

      let updatedCount = 0;
      const selectedIdsBeforeRefresh = this.selected
        .map(item => this.getItemStateKey(item))
        .filter(Boolean);

      try {
        const responses = await Promise.all(requests);
        updatedCount = responses.reduce(
          (acc, response) => acc + (Number(response?.data?.updatedCount) || 0),
          0
        );

        this.applyCategorySetLocalUpdates(eventIds, addTagIds, removeTagIds);

        this.categorySetPending = [];

        await this.$store.dispatch("getEvents", {
          ...this.filter
        });
        await this.$nextTick();
        this.syncSelectedWithLatestEvents(selectedIdsBeforeRefresh);

        this.$store.dispatch(
          "createSuccess",
          `Kategorien für ${this.formatNumberDe(
            updatedCount || eventIds.length
          )} Veranstaltungen gespeichert.`
        );
        this.closeCategorySetDialog();
      } catch (error) {
        const errorText = this.getApiErrorMessage(
          error,
          "Änderungen konnten nicht gespeichert werden. Bitte erneut versuchen."
        );
        this.categorySetSaveError = errorText;
        this.$store.dispatch("createError", {
          text: errorText
        });
      } finally {
        this.categorySetSaving = false;
      }
    },
    async saveFeatureSetChanges() {
      if (this.featureSetSaving) return;

      const eventIds = this.getNormalizedIdList(
        this.featureSetSelection?.ids?.length
          ? this.featureSetSelection.ids
          : this.featureSetItemsLoaded
              .map(item => this.getItemStateKey(item))
              .filter(Boolean)
      );

      if (!eventIds.length) {
        this.$store.dispatch("createError", {
          text: "Keine Änderungen zum Speichern vorhanden."
        });
        return;
      }

      this.featureSetSaving = true;
      this.featureSetSaveError = "";

      const add = this.featureSetPending
        .filter(change => change.type === "add")
        .map(change => change.tag)
        .filter(Boolean);
      const remove = this.featureSetPending
        .filter(change => change.type === "remove")
        .map(change => change.tag)
        .filter(Boolean);

      const addTagIds = [
        ...new Set(
          add.map(tag => this.resolveFeatureSetTagId(tag)).filter(Boolean)
        )
      ];
      const removeTagIds = [
        ...new Set(
          remove.map(tag => this.resolveFeatureSetTagId(tag)).filter(Boolean)
        )
      ];
      const unresolvedTags = [
        ...add.filter(tag => !this.resolveFeatureSetTagId(tag)),
        ...remove.filter(tag => !this.resolveFeatureSetTagId(tag))
      ];

      if (!addTagIds.length && !removeTagIds.length) {
        this.$store.dispatch("createError", {
          text: "Keine gültigen Merkmale zum Speichern gefunden."
        });
        this.featureSetSaving = false;
        return;
      }

      if (unresolvedTags.length) {
        this.$store.dispatch("createError", {
          text: `Einige Merkmale konnten nicht zugeordnet werden: ${[
            ...new Set(unresolvedTags)
          ].join(", ")}`
        });
      }

      const requests = [
        addTagIds.length
          ? this.runBulkTagUpdate({
              ids: eventIds,
              field: "features",
              tagIds: addTagIds,
              action: "add"
            })
          : null,
        removeTagIds.length
          ? this.runBulkTagUpdate({
              ids: eventIds,
              field: "features",
              tagIds: removeTagIds,
              action: "remove"
            })
          : null
      ].filter(Boolean);

      let updatedCount = 0;
      const selectedIdsBeforeRefresh = this.selected
        .map(item => this.getItemStateKey(item))
        .filter(Boolean);

      try {
        const responses = await Promise.all(requests);
        updatedCount = responses.reduce(
          (acc, response) => acc + (Number(response?.data?.updatedCount) || 0),
          0
        );

        this.applyFeatureSetLocalUpdates(eventIds, addTagIds, removeTagIds);

        this.featureSetPending = [];

        await this.$store.dispatch("getEvents", {
          ...this.filter
        });
        await this.$nextTick();
        this.syncSelectedWithLatestEvents(selectedIdsBeforeRefresh);

        this.$store.dispatch(
          "createSuccess",
          `Merkmale für ${this.formatNumberDe(
            updatedCount || eventIds.length
          )} Veranstaltungen gespeichert.`
        );
        this.closeFeatureSetDialog();
      } catch (error) {
        const errorText = this.getApiErrorMessage(
          error,
          "Änderungen konnten nicht gespeichert werden. Bitte erneut versuchen."
        );
        this.featureSetSaveError = errorText;
        this.$store.dispatch("createError", {
          text: errorText
        });
      } finally {
        this.featureSetSaving = false;
      }
    },
    closeCategoryDialog() {
      if (this.suggestingCategories) {
        this.abortSuggestingCategories = true;
      }
      this.categoryDialogOpen = false;
      this.dialogItemsLoaded = [];
      this.clearSelection();
    },
    getEventText(item) {
      const description =
        item?.moment?.description ||
        item?.description ||
        item?.moment?.short_description ||
        item?.short_description ||
        "";
      const channelDescriptions = Object.values(item?.alternative_content || {})
        .map(channelContent => {
          if (!channelContent) return "";
          return [channelContent.short_description, channelContent.description]
            .filter(Boolean)
            .join("\n");
        })
        .filter(Boolean)
        .join("\n\n");

      const fullText = [description, channelDescriptions]
        .filter(Boolean)
        .join("\n\n");
      return stringifyUTF8(fullText || "");
    },
    getPrincipalIdsForScope() {
      const mainPrincipalId = this.selectedPrincipal?.id;
      const subPrincipalIds = (this.selectedPrincipal?.subPrincipals || []).map(
        subPrincipal => subPrincipal.id
      );
      return [mainPrincipalId, ...subPrincipalIds].filter(Boolean);
    },
    // Kategorien werden aus Category-Groups geladen und fuer das Prompt flach gesammelt.
    async loadCategoriesForPrompt() {
      const headers = getTokenHeader();
      const principalIds = this.getPrincipalIdsForScope();
      const res = await axios.get(
        `${env.events_api_client_url}category_group/`,
        {
          params: {
            mainPrincipal: this.selectedPrincipal?.id,
            principal_id: { $in: principalIds }
          },
          headers
        }
      );
      this.categoriesForPrompt = Array.isArray(res.data) ? res.data : [];
    },
    async loadFeaturesForPrompt() {
      this.featuresForPrompt = Array.isArray(this.features)
        ? this.features
        : [];
    },
    async getFullEventForSuggestion(item, context = "UNKNOWN") {
      const eventId = this.getItemStateKey(item);
      if (!eventId) {
        this.logSuggestionFlow("DETAIL_SKIP_NO_EVENT_ID", {
          context,
          itemTitle: this.getTitle(item)
        });
        return item;
      }
      const headers = getTokenHeader();
      const principalIds = this.getPrincipalIdsForScope();
      this.logSuggestionFlow("DETAIL_REQUEST_START", {
        context,
        eventId,
        principalIdsCount: principalIds.length
      });
      const res = await axios.get(
        `${env.events_api_client_url}event/${eventId}`,
        {
          params: {
            principal_id: { $in: principalIds }
          },
          headers
        }
      );
      this.logSuggestionFlow("DETAIL_REQUEST_SUCCESS", {
        context,
        eventId,
        hasAdditionalCategories: Array.isArray(res.data?.additional_categories),
        hasFeatures: Array.isArray(res.data?.features)
      });
      return res.data || item;
    },
    async loadAvailableItemsForDisplay() {
      // Lädt alle verfügbaren Kategorien/Features für korrekte Titel-Anzeige
      if (this.currentSuggestionType === "category") {
        await this.loadCategoriesForPrompt();
      } else {
        await this.loadFeaturesForPrompt();
      }
    },
    async enrichDialogItemsWithDetails() {
      const items = this.dialogItems;
      if (!items || items.length === 0) return;
      this.logSuggestionFlow("ENRICH_START", {
        itemsCount: items.length,
        suggestionType: this.currentSuggestionType
      });

      // Immer neu laden: die Liste kann bereits Felder enthalten, aber veraltete Werte.

      try {
        for (let index = 0; index < items.length; index++) {
          const item = items[index];
          const itemKey = this.getItemStateKey(item);
          if (!itemKey) continue;

          try {
            const fullEvent = await this.getFullEventForSuggestion(
              item,
              "ENRICH_ON_DIALOG_OPEN"
            );
            this.$set(this.dialogItemsLoaded, index, {
              ...item,
              ...(fullEvent || {})
            });
          } catch (error) {
            console.warn(`Failed to enrich item ${itemKey}:`, error.message);
          }
        }
      } catch (error) {
        console.error("Error during dialog items enrichment:", error);
      } finally {
        this.logSuggestionFlow("ENRICH_DONE", {
          itemsCount: items.length
        });
      }
    },
    getPossibleCategoriesPayload() {
      const categoryGroups = Array.isArray(this.categoriesForPrompt)
        ? this.categoriesForPrompt
        : [];

      const allCategories = [
        ...new Set(
          categoryGroups
            .flatMap(group => group?.categories || [])
            .filter(category => category?.isCategoryGroup !== true)
            .map(
              category =>
                category?.title || category?.name || category?.short_title
            )
            .map(title => `${title || ""}`.trim())
            .filter(Boolean)
        )
      ];

      return [
        "Moegliche Kategorien:",
        `Anzahl Kategorien: ${allCategories.length}`,
        allCategories.length ? allCategories.join(", ") : ""
      ].join("\n");
    },
    getPossibleFeaturesPayload() {
      const allFeatures = [
        ...new Set(
          (this.featuresForPrompt || [])
            .map(feature => feature?.title || feature?.name || feature?.label)
            .map(title => `${title || ""}`.trim())
            .filter(Boolean)
        )
      ];

      return [
        "Moegliche Merkmale:",
        `Anzahl Merkmale: ${allFeatures.length}`,
        allFeatures.length ? allFeatures.join(", ") : ""
      ].join("\n");
    },
    getPossibleItemsPayload() {
      return this.currentSuggestionType === "feature"
        ? this.getPossibleFeaturesPayload()
        : this.getPossibleCategoriesPayload();
    },
    async fetchCategorySuggestion(item, preloadedEvent = null) {
      const headers = getTokenHeader() || undefined;
      const user = getCurrentUserIdFromSession(
        this.currentUser,
        localStorage.getItem("session")
      );
      const fullEvent = preloadedEvent || item || {};
      let payload = "";
      try {
        payload = buildSuggestionPromptPayload({
          currentSuggestionType: this.currentSuggestionType,
          title: this.getTitle(fullEvent) || "",
          text: this.getEventText(fullEvent),
          currentLabels: this.getCurrentLabels(fullEvent),
          mainCategoryLabel: this.getMainCategoryLabel(fullEvent),
          possibleItemsPayload: this.getPossibleItemsPayload(),
          entityLabelGenitive: "der Veranstaltung",
          contextPhrase: "zur Veranstaltung"
        });
      } catch (error) {
        const fallbackTitle =
          this.getTitle(fullEvent) || this.getTitle(item) || "";
        payload = `Titel der Veranstaltung: ${fallbackTitle}`;
      }
      const body = {
        principal_id: this.selectedPrincipal?.id,
        promptShortId:
          this.currentSuggestionType === "feature"
            ? FEATURE_SUGGESTION_PROMPT_SHORT_ID
            : CATEGORY_SUGGESTION_PROMPT_SHORT_ID,
        payload,
        user: user || this.currentUser?.id || this.currentUser?._id || ""
      };
      return executeSuggestionPrompt({
        promptsApiUrl: env.prompts_api_client_url,
        body,
        headers,
        onLog: (step, meta) => this.logSuggestionFlow(step, meta),
        logMeta: {
          itemKey: this.getItemStateKey(fullEvent || item)
        }
      });
    },
    removeSuggestedCategory(item, index) {
      const itemKey = this.getSuggestionMapKey(item);
      if (!itemKey) return;
      const suggestions = this.suggestedCategoriesById[itemKey] || [];
      if (!Array.isArray(suggestions)) return;
      if (index < 0 || index >= suggestions.length) return;

      const updatedSuggestions = [...suggestions];
      updatedSuggestions.splice(index, 1);
      this.$set(this.suggestedCategoriesById, itemKey, updatedSuggestions);
    },
    async applySingleSuggestedCategory(item, label, index) {
      const itemKey = this.getSuggestionMapKey(item);
      const normalizedLabel = `${label || ""}`.trim();
      if (!itemKey || !normalizedLabel) return;

      const mappedId = this.resolveSuggestionLabelToId(normalizedLabel);
      if (!mappedId) {
        this.$store.dispatch("createError", {
          text: `${this.suggestionTypeSingularLabel} "${normalizedLabel}" konnte nicht zugeordnet werden.`
        });
        return;
      }

      const wasAdded = this.appendMappedIdToCurrentItem(item, mappedId);
      if (!wasAdded) return;

      try {
        const currentValues =
          this.currentSuggestionType === "feature"
            ? Array.isArray(item.features)
              ? item.features
              : []
            : Array.isArray(item.additional_categories)
            ? item.additional_categories
            : [];
        const currentIds = currentValues
          .map(value => this.normalizeEntityId(value))
          .filter(Boolean);

        if (this.currentSuggestionType === "feature") {
          const persistedIds = await this.saveFeaturesForItem(item, currentIds);
          this.$set(item, "features", persistedIds);
        } else {
          const persistedIds = await this.saveAdditionalCategoriesForItem(
            item,
            currentIds
          );
          this.$set(item, "additional_categories", persistedIds);
        }
        this.$delete(this.pendingSaveById, itemKey);
        this.$set(this.appliedSuggestionsById, itemKey, true);
      } catch (error) {
        const errorText =
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          "Unbekannter Fehler";
        this.$store.dispatch("createError", {
          text: `Speichern fehlgeschlagen: ${errorText}`
        });
        return;
      }

      const suggestions = this.suggestedCategoriesById[itemKey] || [];
      const updatedSuggestions = [...suggestions];
      if (index >= 0 && index < updatedSuggestions.length) {
        updatedSuggestions.splice(index, 1);
      } else {
        const fallbackIndex = updatedSuggestions.findIndex(
          entry => `${entry || ""}`.trim() === normalizedLabel
        );
        if (fallbackIndex >= 0) {
          updatedSuggestions.splice(fallbackIndex, 1);
        }
      }

      this.$set(this.suggestedCategoriesById, itemKey, updatedSuggestions);
      if (updatedSuggestions.length === 0) {
        this.$delete(this.suggestionErrorsById, itemKey);
      }

      this.$store.dispatch(
        "createSuccess",
        `${this.suggestionTypeSingularLabel} gespeichert.`
      );
    },
    async suggestCategoriesForItem(item) {
      if (!item || this.suggestingCategories || this.applyingCategories) return;
      if (!this.selectedPrincipal?.id) {
        this.$store.dispatch("createError", {
          text: "Kein Hauptmandant ausgewaehlt."
        });
        return;
      }

      const itemKey = this.getSuggestionMapKey(item);
      if (!itemKey) return;

      this.suggestingItemId = itemKey;
      this.$delete(this.suggestionErrorsById, itemKey);

      try {
        if (this.currentSuggestionType === "feature") {
          if (!this.featuresForPrompt.length) {
            await this.loadFeaturesForPrompt();
          }
        } else if (!this.categoriesForPrompt.length) {
          await this.loadCategoriesForPrompt();
        }

        const fullEvent = await this.getFullEventForSuggestion(
          item,
          "SUGGEST_SINGLE_ITEM"
        );

        // Vor jeder Suggestion den aktuellen Server-Stand in die Dialogzeile uebernehmen.
        const sourceItemKey = this.getItemStateKey(item);
        const dialogIndex = this.dialogItems.findIndex(
          dialogItem => this.getItemStateKey(dialogItem) === sourceItemKey
        );
        if (dialogIndex >= 0) {
          this.$set(this.dialogItemsLoaded, dialogIndex, {
            ...(this.dialogItemsLoaded[dialogIndex] || item),
            ...(fullEvent || {})
          });
        }

        const suggestions = await this.fetchCategorySuggestion(
          fullEvent,
          fullEvent
        );
        const resolvedDialogItem =
          dialogIndex >= 0 ? this.dialogItemsLoaded[dialogIndex] : fullEvent;
        const blockedLabels = new Set(
          this.getCurrentLabels(resolvedDialogItem)
            .map(label => this.normalizeCategoryLabel(label))
            .filter(Boolean)
        );
        if (this.currentSuggestionType === "category") {
          const mainCategoryLabel = this.getMainCategoryLabel(
            resolvedDialogItem
          );
          const normalizedMainLabel = this.normalizeCategoryLabel(
            mainCategoryLabel
          );
          if (normalizedMainLabel) {
            blockedLabels.add(normalizedMainLabel);
          }
        }

        const filteredSuggestions = suggestions.filter(label => {
          return !blockedLabels.has(this.normalizeCategoryLabel(label));
        });
        const existingSuggestions = (
          this.suggestedCategoriesById[itemKey] || []
        ).filter(
          label => !blockedLabels.has(this.normalizeCategoryLabel(label))
        );
        const mergedSuggestions = [
          ...new Set([...existingSuggestions, ...filteredSuggestions])
        ];
        this.$set(this.suggestedCategoriesById, itemKey, mergedSuggestions);
      } catch (error) {
        const errorText =
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          "Unbekannter Fehler";
        this.$set(
          this.suggestionErrorsById,
          itemKey,
          `Vorschlag-Request fehlgeschlagen: ${errorText}`
        );
      } finally {
        this.suggestingItemId = null;
      }
    },
    resolveSuggestionLabelToId(label) {
      return this.currentSuggestionType === "feature"
        ? this.getFeatureIdByLabel(label)
        : this.getCategoryIdByLabel(label);
    },
    appendMappedIdToCurrentItem(item, mappedId) {
      const itemKey = this.getItemStateKey(item);
      if (!itemKey) {
        return false;
      }

      const fieldName =
        this.currentSuggestionType === "feature"
          ? "features"
          : "additional_categories";

      const currentValues = Array.isArray(item?.[fieldName])
        ? item[fieldName]
        : [];
      const currentIds = currentValues
        .map(value => this.normalizeEntityId(value))
        .filter(Boolean);

      if (currentIds.includes(mappedId)) {
        return false;
      }

      this.$set(item, fieldName, [...currentIds, mappedId]);
      this.$set(this.pendingSaveById, itemKey, true);
      this.$set(this.appliedSuggestionsById, itemKey, true);
      return true;
    },
    normalizeCategoryLabel(label) {
      return normalizeLabel(label);
    },
    getCategoryIdByLabel(label) {
      const normalizedLabel = this.normalizeCategoryLabel(label);
      if (!normalizedLabel) return null;

      const directMatch = (this.categories || []).find(category =>
        [category?.title, category?.name, category?.short_title]
          .map(value => this.normalizeCategoryLabel(value))
          .includes(normalizedLabel)
      );

      return this.normalizeEntityId(directMatch) || null;
    },
    mapSuggestionLabelsToCategoryIds(suggestions) {
      const missingLabels = [];
      const mappedIds = [];

      suggestions.forEach(label => {
        const categoryId = this.getCategoryIdByLabel(label);
        if (categoryId) {
          mappedIds.push(categoryId);
        } else {
          missingLabels.push(label);
        }
      });

      return {
        ids: [...new Set(mappedIds)],
        missingLabels
      };
    },
    getFeatureIdByLabel(label) {
      const normalizedLabel = this.normalizeCategoryLabel(label);
      if (!normalizedLabel) return null;

      const directMatch = (this.features || []).find(feature =>
        [feature?.title, feature?.name, feature?.label]
          .map(value => this.normalizeCategoryLabel(value))
          .includes(normalizedLabel)
      );

      return this.normalizeEntityId(directMatch) || null;
    },
    mapSuggestionLabelsToFeatureIds(suggestions) {
      const missingLabels = [];
      const mappedIds = [];

      suggestions.forEach(label => {
        const featureId = this.getFeatureIdByLabel(label);
        if (featureId) {
          mappedIds.push(featureId);
        } else {
          missingLabels.push(label);
        }
      });

      return {
        ids: [...new Set(mappedIds)],
        missingLabels
      };
    },
    mapSuggestionLabelsToIds(suggestions) {
      if (this.currentSuggestionType === "feature") {
        return this.mapSuggestionLabelsToFeatureIds(suggestions);
      }
      return this.mapSuggestionLabelsToCategoryIds(suggestions);
    },
    getNormalizedIdList(values) {
      return getNormalizedIdList(values, value =>
        this.normalizeEntityId(value)
      );
    },
    getApiErrorMessage(error, fallback = "Unbekannter Fehler") {
      return (
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        fallback
      );
    },
    async bulkReadEventsByIds(ids = []) {
      const eventIds = this.getNormalizedIdList(ids);
      if (!eventIds.length) return {};

      const principalId = this.selectedPrincipal?.id;
      const requestConfig = {
        headers: getTokenHeader(),
        params: principalId ? { principal_id: principalId } : undefined
      };
      const response = await axios.post(
        `${env.events_api_client_url}event/bulk-read`,
        {
          ids: eventIds
        },
        requestConfig
      );

      const events = Array.isArray(response?.data?.events)
        ? response.data.events
        : [];
      return events.reduce((acc, event) => {
        const id = this.getItemStateKey(event);
        if (id) {
          acc[id] = event;
        }
        return acc;
      }, {});
    },
    async runBulkTagUpdate({ ids = [], field, tagIds = [], action }) {
      const principalId = this.selectedPrincipal?.id;
      await runBulkTagUpdateRequest({
        apiUrl: `${env.events_api_client_url}event/bulk-tags`,
        ids,
        field,
        tagIds,
        action,
        headers: getTokenHeader(),
        params: principalId ? { principal_id: principalId } : undefined,
        getId: value => this.getItemStateKey(value)
      });
    },
    async saveAdditionalCategoriesForItem(item, additionalCategoryIds) {
      const fullEvent = await this.getFullEventForSuggestion(item);
      const eventId = this.getItemStateKey(fullEvent || item);
      const uniqueAdditionalCategoryIds = this.getNormalizedIdList(
        additionalCategoryIds
      );
      const currentIds = this.getNormalizedIdList(
        fullEvent?.additional_categories || []
      );

      const idsToAdd = uniqueAdditionalCategoryIds.filter(
        id => !currentIds.includes(id)
      );
      const idsToRemove = currentIds.filter(
        id => !uniqueAdditionalCategoryIds.includes(id)
      );

      if (idsToAdd.length) {
        await this.runBulkTagUpdate({
          ids: [eventId],
          field: "additional_categories",
          tagIds: idsToAdd,
          action: "add"
        });
      }
      if (idsToRemove.length) {
        await this.runBulkTagUpdate({
          ids: [eventId],
          field: "additional_categories",
          tagIds: idsToRemove,
          action: "remove"
        });
      }

      return uniqueAdditionalCategoryIds;
    },
    async saveFeaturesForItem(item, featureIds) {
      const fullEvent = await this.getFullEventForSuggestion(item);
      const eventId = this.getItemStateKey(fullEvent || item);
      const uniqueFeatureIds = this.getNormalizedIdList(featureIds);
      const currentIds = this.getNormalizedIdList(fullEvent?.features || []);

      const idsToAdd = uniqueFeatureIds.filter(id => !currentIds.includes(id));
      const idsToRemove = currentIds.filter(
        id => !uniqueFeatureIds.includes(id)
      );

      if (idsToAdd.length) {
        await this.runBulkTagUpdate({
          ids: [eventId],
          field: "features",
          tagIds: idsToAdd,
          action: "add"
        });
      }
      if (idsToRemove.length) {
        await this.runBulkTagUpdate({
          ids: [eventId],
          field: "features",
          tagIds: idsToRemove,
          action: "remove"
        });
      }

      return uniqueFeatureIds;
    },
    async applySuggestionsForItem(item) {
      if (!item || this.suggestingCategories || this.applyingCategories) return;
      const itemKey = this.getSuggestionMapKey(item);
      const suggestions = this.suggestedCategoriesById[itemKey] || [];
      if (!suggestions.length) return;

      const { ids, missingLabels } = this.mapSuggestionLabelsToIds(suggestions);
      if (!ids.length) {
        this.$set(
          this.suggestionErrorsById,
          itemKey,
          `Keine ${this.suggestionTypeLabel.toLowerCase()} gefunden: ${missingLabels.join(
            ", "
          )}`
        );
        return;
      }

      const currentValues =
        this.currentSuggestionType === "feature"
          ? Array.isArray(item.features)
            ? item.features
            : []
          : Array.isArray(item.additional_categories)
          ? item.additional_categories
          : [];
      const currentIds = currentValues
        .map(value => this.normalizeEntityId(value))
        .filter(Boolean);
      const nextIds = [...new Set([...currentIds, ...ids])];

      try {
        if (this.currentSuggestionType === "feature") {
          const persistedIds = await this.saveFeaturesForItem(item, nextIds);
          this.$set(item, "features", persistedIds);
        } else {
          const persistedIds = await this.saveAdditionalCategoriesForItem(
            item,
            nextIds
          );
          this.$set(item, "additional_categories", persistedIds);
        }
      } catch (error) {
        const errorText =
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          "Unbekannter Fehler";
        this.$set(
          this.suggestionErrorsById,
          itemKey,
          `Speichern fehlgeschlagen: ${errorText}`
        );
        this.$store.dispatch("createError", {
          text: `Speichern fehlgeschlagen: ${errorText}`
        });
        return;
      }

      this.$set(this.appliedSuggestionsById, itemKey, true);
      this.$delete(this.pendingSaveById, itemKey);
      if (missingLabels.length) {
        this.$set(
          this.suggestionErrorsById,
          itemKey,
          `Teilweise übernommen. Nicht gefunden: ${missingLabels.join(", ")}`
        );
        this.$set(this.suggestedCategoriesById, itemKey, missingLabels);
      } else {
        this.$delete(this.suggestionErrorsById, itemKey);
        this.$set(this.suggestedCategoriesById, itemKey, []);
      }

      this.$store.dispatch(
        "createSuccess",
        `${ids.length} ${this.suggestionTypeLabel.toLowerCase()} gespeichert.`
      );
    },
    discardSuggestionsForItem(item) {
      const itemKey = this.getSuggestionMapKey(item);
      if (!itemKey) return;
      this.$set(this.suggestedCategoriesById, itemKey, []);
      this.$delete(this.suggestionErrorsById, itemKey);
      this.$set(this.appliedSuggestionsById, itemKey, false);
    },
    async handleCategoryDialogAction() {
      this.closeCategoryDialog();
    },
    // eslint-disable-next-line no-unused-vars
    // Legacy-Flow bleibt vorerst im Code, ist im vereinfachten Dialog aktuell nicht aktiv verdrahtet.
    async suggestCategories() {
      this.logSuggestionFlow("FLOW_START", {
        itemsCount: this.dialogItems.length,
        suggestionType: this.currentSuggestionType,
        alreadyRunning: this.suggestingCategories,
        selectedPrincipalId: this.selectedPrincipal?.id || null
      });
      if (!this.dialogItems.length || this.suggestingCategories) {
        this.logSuggestionFlow("FLOW_EXIT_EARLY", {
          reason: !this.dialogItems.length
            ? "NO_DIALOG_ITEMS"
            : "ALREADY_RUNNING"
        });
        return;
      }
      if (!this.selectedPrincipal?.id) {
        this.logSuggestionFlow("FLOW_EXIT_EARLY", {
          reason: "NO_SELECTED_PRINCIPAL"
        });
        this.$store.dispatch("createError", {
          text: "Kein Hauptmandant ausgewaehlt."
        });
        return;
      }

      this.suggestingCategories = true;
      this.suggestionsCompleted = false;
      this.suggestingIndex = -1;
      this.suggestingItemId = null;
      this.abortSuggestingCategories = false;
      this.suggestedCategoriesById = {};
      this.suggestionErrorsById = {};
      this.categoriesForPrompt = [];
      this.featuresForPrompt = [];
      let wasAborted = false;
      let hasFatalError = false;

      try {
        if (this.currentSuggestionType === "feature") {
          this.logSuggestionFlow("LOAD_PROMPT_ITEMS_START", {
            type: "feature"
          });
          await this.loadFeaturesForPrompt();
        } else {
          this.logSuggestionFlow("LOAD_PROMPT_ITEMS_START", {
            type: "category"
          });
          await this.loadCategoriesForPrompt();
        }
        this.logSuggestionFlow("LOAD_PROMPT_ITEMS_DONE", {
          categoriesForPrompt: this.categoriesForPrompt.length,
          featuresForPrompt: this.featuresForPrompt.length
        });
        for (let i = 0; i < this.dialogItems.length; i++) {
          if (this.abortSuggestingCategories) {
            this.logSuggestionFlow("FLOW_ABORTED", {
              index: i
            });
            wasAborted = true;
            break;
          }

          const item = this.dialogItems[i];
          const itemKey = this.getItemSuggestionStateKey(item, i);
          this.suggestingIndex = i;
          this.suggestingItemId = itemKey;
          this.logSuggestionFlow("ITEM_START", {
            index: i,
            itemKey,
            itemTitle: this.getTitle(item)
          });

          let eventForSuggestion = item;
          try {
            // 1) Detaildaten neu laden
            const fullEvent = await this.getFullEventForSuggestion(
              item,
              "SUGGEST_LOOP"
            );
            eventForSuggestion = fullEvent || item;
            const currentFieldName =
              this.currentSuggestionType === "feature"
                ? "features"
                : "additional_categories";
            const localCurrentValues = Array.isArray(item?.[currentFieldName])
              ? [...item[currentFieldName]]
              : null;
            const mergedDialogItem = {
              ...item,
              ...(eventForSuggestion || {})
            };

            // Lokale manuelle Zuordnungen im Dialog behalten (z.B. nach "Neu erzeugen").
            if (Array.isArray(localCurrentValues)) {
              mergedDialogItem[currentFieldName] = localCurrentValues;
            }

            this.$set(this.dialogItemsLoaded, i, mergedDialogItem);
            this.logSuggestionFlow("ITEM_DETAIL_READY", {
              index: i,
              itemKey
            });
            // 2) GUI mit den Detaildaten aktualisieren
            await this.$nextTick();
            this.logSuggestionFlow("ITEM_DETAIL_RENDERED", {
              index: i,
              itemKey
            });
          } catch (error) {
            const errorText =
              error?.response?.data?.message ||
              error?.response?.data?.error ||
              error?.message ||
              "Unbekannter Fehler";
            this.logSuggestionFlow("ITEM_DETAIL_FAILED", {
              index: i,
              itemKey,
              error: errorText
            });
            this.$set(
              this.suggestionErrorsById,
              itemKey,
              `Detail-Laden fehlgeschlagen: ${errorText}`
            );
            await this.$nextTick();
            continue;
          }

          if (this.abortSuggestingCategories) {
            this.logSuggestionFlow("FLOW_ABORTED", {
              index: i,
              itemKey,
              phase: "BEFORE_SUGGEST_REQUEST"
            });
            wasAborted = true;
            break;
          }

          try {
            // 3) Vorschläge laden (immer nach erfolgreichem Detail-Reload)
            this.logSuggestionFlow("ITEM_SUGGEST_START", {
              index: i,
              itemKey
            });
            const suggestions = await this.fetchCategorySuggestion(
              eventForSuggestion,
              eventForSuggestion
            );
            this.$set(this.suggestedCategoriesById, itemKey, [...suggestions]);
            this.$delete(this.suggestionErrorsById, itemKey);
            this.logSuggestionFlow("ITEM_SUGGEST_SUCCESS", {
              index: i,
              itemKey,
              suggestionsCount: suggestions.length
            });
          } catch (error) {
            const errorText =
              error?.response?.data?.message ||
              error?.response?.data?.error ||
              error?.message ||
              "Unbekannter Fehler";
            this.logSuggestionFlow("ITEM_SUGGEST_FAILED", {
              index: i,
              itemKey,
              error: errorText
            });
            this.$set(
              this.suggestionErrorsById,
              itemKey,
              `Vorschlag-Request fehlgeschlagen: ${errorText}`
            );
          } finally {
            // 4) GUI mit den Vorschlägen aktualisieren
            await this.$nextTick();
            this.logSuggestionFlow("ITEM_DONE", {
              index: i,
              itemKey
            });
          }
        }
      } catch (error) {
        hasFatalError = true;
        const errorText =
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          "Unbekannter Fehler";
        this.logSuggestionFlow("FLOW_FATAL_ERROR", {
          error: errorText
        });
        this.$store.dispatch("createError", {
          text: `${this.suggestionTypeLabel} konnten nicht vorgeschlagen werden: ${errorText}`
        });
      } finally {
        this.suggestingCategories = false;
        this.suggestingIndex = -1;
        this.suggestingItemId = null;
        this.abortSuggestingCategories = false;
        this.logSuggestionFlow("FLOW_FINALLY", {
          wasAborted,
          hasFatalError,
          errorCount: Object.keys(this.suggestionErrorsById).length
        });
      }

      this.suggestionsCompleted = !wasAborted && !hasFatalError;

      if (Object.keys(this.suggestionErrorsById).length) {
        const failedCount = Object.keys(this.suggestionErrorsById).length;
        this.$store.dispatch("createError", {
          text: `Einige ${this.suggestionTypeLabel.toLowerCase()} konnten nicht vorgeschlagen werden (${failedCount}/${
            this.dialogItems.length
          }).`
        });
      }
    },
    getCategoryTitle(categoryValue) {
      return resolveLabelFromValue(categoryValue, {
        resolveTitleById: id => this.categoriesById[id] || "",
        getId: value => this.normalizeEntityId(value),
        labelKeys: ["title", "name", "short_title"],
        allowRawString: false
      });
    },
    getCurrentCategoryLabels(item) {
      if (!item) return [];
      const categoryValues = Array.isArray(item.additional_categories)
        ? [...item.additional_categories]
        : [];

      // Debug: Zeige Warnungen für fehlende Kategorien
      if (
        this.categoryDialogOpen &&
        !this._debugLogged &&
        categoryValues.length > 0
      ) {
        const notFound = categoryValues.filter(id => !this.categoriesById[id]);
        if (notFound.length > 0) {
          console.warn(
            `${notFound.length} category IDs not found in categoriesById:`,
            notFound
          );
        }
        this._debugLogged = true;
      }

      return extractResolvedLabels(categoryValues, {
        resolveTitleById: id => this.categoriesById[id] || "",
        getId: value => this.normalizeEntityId(value),
        labelKeys: ["title", "name", "short_title"],
        allowRawString: false
      });
    },
    getFeatureTitle(featureValue) {
      return resolveLabelFromValue(featureValue, {
        resolveTitleById: id => this.featuresById[id] || "",
        getId: value => this.normalizeEntityId(value),
        labelKeys: ["title", "name", "label"],
        allowRawString: false
      });
    },
    getCurrentFeatureLabels(item) {
      if (!item) return [];
      const features = Array.isArray(item.features) ? item.features : [];
      return extractResolvedLabels(features, {
        resolveTitleById: id => this.featuresById[id] || "",
        getId: value => this.normalizeEntityId(value),
        labelKeys: ["title", "name", "label"],
        allowRawString: false
      });
    },
    getCurrentLabels(item) {
      if (this.currentSuggestionType === "feature") {
        return this.getCurrentFeatureLabels(item);
      }
      return this.getCurrentCategoryLabels(item);
    },
    editHelper(item) {
      this.$emit("editEvent", item);
    },
    deleteHelper(item) {
      if (!item.deleted) this.$emit("deleteEvent", item);
    },
    copyHelper(item) {
      if (!item.deleted) this.$emit("copyEvent", item);
    },
    checkBoxHighlightHelper(item) {
      if (!item.deleted) {
        item.highlight[this.config.useHighlightFrom] = !item.highlight[
          this.config.useHighlightFrom
        ];
        this.$emit("updateCheckboxState", {
          item,
          prop: `highlight.${this.config.useHighlightFrom}`
        });
      }
    },
    checkBoxActiveHelper(item) {
      if (!item.deleted)
        this.$emit("updateCheckboxState", { item, prop: "active" });
    },
    checkBoxCancelledHelper(item) {
      if (!item.deleted)
        this.$emit("updateCheckboxState", { item, prop: "cancelled" });
    },
    checkBoxMomentCancelledHelper(item) {
      if (!item.deleted)
        this.$emit("updateCheckboxStateMoment", { item, prop: "cancelled" });
    },
    checkIfDateIsInPast(item) {
      const date = new Date(item.moment.from);
      const now = new Date().setHours(0, 0, 0, 0);
      if (date < now) return true;
    },
    checkIfMomentIsCancelled(item) {
      return (
        item.moment?.cancelled ??
        (item.cancelled && !this.checkIfDateIsInPast(item))
      );
    },
    commonClasses(item) {
      return [
        item.deleted ? "deleted__event" : "",
        this.checkIfDateIsInPast(item) ? "in__past" : "event__title",
        item.active ? "" : "inactive__event",
        item.cancelled && !this.checkIfDateIsInPast(item)
          ? "cancelled__event"
          : "",
        this.checkIfMomentIsCancelled(item) ? "cancelled__event" : "",
        item.regionality.includes("internal") ? "intern__event" : ""
      ];
    },
    checkDuplicate(duplicate) {
      let apiKeyDuplicate = false;
      if (duplicate?.apiKeys?.length) {
        const eventConfig = this.$store?.state?.eventConfig || this.config;
        if (eventConfig) {
          const currentApiKeys = eventConfig?.apiKeys.map(ak => ak.apiKey);
          // any match between currentApiKeys and duplicate.apiKeys?
          apiKeyDuplicate = duplicate.apiKeys.some(ak =>
            currentApiKeys.includes(ak)
          );
        } else {
          // cant check for duplicates without eventConfig,
          // but it might be a duplicate. so return true
          console.info("No eventConfig found. Cant check for duplicates.");
          apiKeyDuplicate = true;
        }
      }
      if (apiKeyDuplicate) return true;
      if (duplicate?.principal) {
        // cant check for duplicates without principals,
        // but it might be a duplicate. so return true
        if (!this.$store.state.principals) {
          console.info("No principals found. Cant check for duplicates.");
          return true;
        }
        return this.$store.state.principals.find(
          p => p._id === duplicate?.principal
        );
      }
      return false;
    },
    stringifyDuplicate(duplicate) {
      if (duplicate.principal)
        return "Mandant: " + this.getPrincipalName(duplicate.principal);
      if (duplicate.apiKeys?.length) {
        return (
          "API-Keys: " +
            duplicate.apiKeys
              .map(ak => {
                return this.getApiKeyDescription(ak);
              })
              .join(", ") || ""
        );
      }
    }
  },
  computed: {
    uniqueEvents() {
      const items = Array.isArray(this.events) ? this.events : [];
      const seenIds = new Set();

      return items.filter(item => {
        const id = item?._id;
        if (!id) return true;
        if (seenIds.has(id)) return false;
        seenIds.add(id);
        return true;
      });
    },
    dialogItems() {
      return this.dialogItemsLoaded;
    },
    categoriesById() {
      const result = {};

      // Quelle 1: categories prop (vom Parent übergeben)
      (this.categories || []).forEach(category => {
        if (!category) return;
        const categoryId = this.normalizeEntityId(category);
        if (!categoryId) return;
        const categoryTitle =
          category.title || category.name || category.short_title || "";
        if (categoryTitle) {
          result[categoryId] = categoryTitle;
        }
      });

      // Quelle 2: categoriesForPrompt (alle Category-Groups für AI-Suggestions)
      // Diese enthält ALLE verfügbaren Kategorien, auch von anderen Mandanten
      (this.categoriesForPrompt || []).forEach(categoryGroup => {
        if (!categoryGroup?.categories) return;
        categoryGroup.categories.forEach(category => {
          if (!category) return;
          const categoryId = this.normalizeEntityId(category);
          if (!categoryId) return;
          const categoryTitle =
            category.title || category.name || category.short_title || "";
          if (categoryTitle && !result[categoryId]) {
            result[categoryId] = categoryTitle;
          }
        });
      });

      return result;
    },
    featuresById() {
      const result = {};

      // Quelle 1: features prop (vom Parent übergeben)
      (this.features || []).forEach(feature => {
        if (!feature) return;
        const featureId = this.normalizeEntityId(feature);
        if (!featureId) return;
        const featureTitle =
          feature.title || feature.name || feature.label || "";
        if (featureTitle) {
          result[featureId] = featureTitle;
        }
      });

      // Quelle 2: featuresForPrompt (alle Features für AI-Suggestions)
      (this.featuresForPrompt || []).forEach(feature => {
        if (!feature) return;
        const featureId = this.normalizeEntityId(feature);
        if (!featureId) return;
        const featureTitle =
          feature.title || feature.name || feature.label || "";
        if (featureTitle && !result[featureId]) {
          result[featureId] = featureTitle;
        }
      });

      return result;
    },
    suggestionTypeLabel() {
      return this.currentSuggestionType === "feature"
        ? "Merkmale"
        : "Kategorien";
    },
    dialogTitleLabel() {
      return "Vorschläge prüfen";
    },
    suggestionTypeSingularLabel() {
      return this.currentSuggestionType === "feature" ? "Merkmal" : "Kategorie";
    },
    dialogSubtitleLabel() {
      return `${this.dialogItems.length} Veranstaltungen`;
    },
    totalSuggestionCount() {
      return this.dialogItems.reduce((acc, item) => {
        return acc + this.getSuggestionsForItem(item).length;
      }, 0);
    },
    currentItemsCount() {
      return this.dialogItems.reduce(
        (acc, item) => acc + this.getCurrentLabels(item).length,
        0
      );
    },
    dialogStatusLabel() {
      if (this.totalSuggestionCount <= 0) return "";
      return `${this.totalSuggestionCount} Vorschläge offen`;
    },
    actionButtonLabel() {
      return "Abschließen";
    },
    hasSuggestionsToApply() {
      return this.totalSuggestionCount > 0;
    },
    currentUser() {
      return this.$store.state.currentUser;
    },
    selectionLabel() {
      if (this.allPagesSelected) {
        return `Alle ${this.eventCount} Veranstaltungen ausgewählt`;
      }
      return `${this.selected.length} von ${this.eventCount} ausgewählt`;
    },
    selectedPrincipal() {
      return this.$store.state.selectedPrincipal;
    },
    channels() {
      if (this.config?.channels?.join("") === "web") return []; // only web-channel => hide
      const activeStandardChannels = this.config?.standard_channels?.filter(c =>
        this.config?.channels?.includes(c.value)
      );
      let activeOwnChannels = this.config?.own_channels?.filter(c =>
        this.config?.channels?.includes(c.value)
      );
      return activeStandardChannels.concat(activeOwnChannels);
    },
    ownChannels() {
      return this.channels.filter(channel =>
        this.config.own_channels?.some(
          ownChannel => ownChannel.value === channel.value
        )
      );
    },
    tableOptions: {
      get() {
        // eslint-disable-next-line no-unused-vars
        const { s, ...filter } = this.filter;
        return filter;
      },
      set(value) {
        if (!this.filter.isDefault) delete value.isDefault;

        const diff = getObjectDiff(this.filter, value);
        if (!diff.length) return;

        value.limit = value.itemsPerPage;
        const sortDirection = value?.sortDesc?.[0] ? "desc" : "asc";

        value.sort = value?.sortBy?.[0] ?? "start";
        value.direction = sortDirection;
        this.$emit("applyFilter", {
          ...this.filter,
          ...value
        });
      }
    },
    activeImports() {
      return imports
        .filter(_ => _.isActive(this.selectedPrincipal))
        .map(_ => _.typeId);
    },
    categorySetGroups() {
      const mode = this.categorySetMode;
      const query = this.normalizeCategoryLabel(this.categorySetQuery);
      const pending = this.categorySetPending;

      const groups = (this.categoriesForPrompt || [])
        .map((group, groupIndex) => {
          const groupId =
            this.normalizeEntityId(group) || `group_${groupIndex}`;
          const groupTitle = `${group?.title || group?.name || ""}`.trim();
          const rawCategories = Array.isArray(group?.categories)
            ? group.categories
            : [];
          const groupCategoryEntity = rawCategories.find(
            category => category?.isCategoryGroup === true
          );
          const rootEntity = groupCategoryEntity || group;
          const rootId = this.normalizeEntityId(rootEntity);
          const childCategoryEntities = rawCategories.filter(
            category => category?.isCategoryGroup !== true
          );
          const groupTag = `${rootEntity?.title ||
            rootEntity?.name ||
            rootEntity?.short_title ||
            groupTitle}`.trim();

          const children = childCategoryEntities
            .map(category => {
              const tag = `${category?.title ||
                category?.name ||
                category?.short_title ||
                ""}`.trim();
              if (!tag) return null;
              if (!this.isCategoryTagVisible(tag)) return null;
              const stats = this.getCategorySetStats(tag);
              return {
                id: `${groupId}::${tag}`,
                tag,
                title: tag,
                checked: pending.some(
                  pendingEntry =>
                    pendingEntry.tag === tag && pendingEntry.type === mode
                ),
                countLabel:
                  mode === "remove"
                    ? `${this.formatNumberDe(
                        stats.has
                      )} von ${this.formatNumberDe(stats.total)}`
                    : ""
              };
            })
            .filter(Boolean);

          const groupVisible = this.isCategoryTagVisible(groupTag);
          const groupSelectable = Boolean(groupTag && rootId);
          const groupStats = this.getCategorySetStats(groupTag);
          const groupChecked = pending.some(
            pendingEntry =>
              pendingEntry.tag === groupTag && pendingEntry.type === mode
          );
          const groupCountLabel =
            mode === "remove" && groupVisible
              ? `${this.formatNumberDe(
                  groupStats.has
                )} von ${this.formatNumberDe(groupStats.total)}`
              : "";

          const groupMatches = this.normalizeCategoryLabel(groupTitle).includes(
            query
          );
          const visibleChildren = query
            ? groupMatches
              ? children
              : children.filter(child =>
                  this.normalizeCategoryLabel(child.title).includes(query)
                )
            : children;

          if (query && !groupMatches && !visibleChildren.length) return null;
          if (!query && !visibleChildren.length && !groupSelectable)
            return null;

          return {
            id: groupId,
            title: groupTitle,
            tag: groupTag,
            selectable: groupSelectable,
            checked: groupChecked,
            countLabel: groupCountLabel,
            isCustomGroup: false,
            entries: visibleChildren
          };
        })
        .filter(Boolean);

      if (!this.categorySetIsAllPages) {
        const customTagsSource = this.categorySetCustomTags.length
          ? this.categorySetCustomTags
          : this.categorySetFallbackCustomTags;
        const customEntries = customTagsSource
          .map(tag => `${tag || ""}`.trim())
          .filter(Boolean)
          .filter(tag => this.isCategoryTagVisible(tag))
          .map(tag => {
            const stats = this.getCategorySetStats(tag);
            return {
              id: `custom::${tag}`,
              tag,
              title: tag,
              checked: pending.some(
                pendingEntry =>
                  pendingEntry.tag === tag && pendingEntry.type === mode
              ),
              countLabel:
                mode === "remove"
                  ? `${this.formatNumberDe(
                      stats.has
                    )} von ${this.formatNumberDe(stats.total)}`
                  : ""
            };
          })
          .filter(entry => {
            if (!query) return true;
            return this.normalizeCategoryLabel(entry.title).includes(query);
          });

        if (customEntries.length) {
          groups.push({
            id: "custom-group",
            title: "Eigene Einträge",
            tag: "",
            selectable: false,
            checked: false,
            countLabel: "",
            isCustomGroup: true,
            entries: customEntries
          });
        }
      }

      return groups;
    },
    categorySetKnownTags() {
      const fromCatalog = (this.categoriesForPrompt || []).flatMap(group => {
        const groupTitle = `${group?.title || group?.name || ""}`.trim();
        const categoryTitles = (group?.categories || [])
          .map(category =>
            `${category?.title ||
              category?.name ||
              category?.short_title ||
              ""}`.trim()
          )
          .filter(Boolean);
        return [groupTitle, ...categoryTitles].filter(Boolean);
      });
      return [...new Set(fromCatalog)];
    },
    categorySetTagIdByNormalizedTag() {
      const map = {};
      const register = (label, idCandidate) => {
        const normalizedLabel = this.normalizeCategoryLabel(label);
        const normalizedId = this.normalizeEntityId(idCandidate);
        if (!normalizedLabel || !normalizedId || map[normalizedLabel]) return;
        map[normalizedLabel] = normalizedId;
      };

      (this.categoriesForPrompt || []).forEach(group => {
        const groupCategories = Array.isArray(group?.categories)
          ? group.categories
          : [];
        const groupCategory = groupCategories.find(
          category => category?.isCategoryGroup === true
        );
        const rootEntity = groupCategory || group;
        const rootTitle = `${rootEntity?.title ||
          rootEntity?.name ||
          rootEntity?.short_title ||
          ""}`.trim();
        register(rootTitle, rootEntity);

        const groupTitle = `${group?.title || group?.name || ""}`.trim();
        register(groupTitle, rootEntity);

        (group?.categories || []).forEach(category => {
          const label = `${category?.title ||
            category?.name ||
            category?.short_title ||
            ""}`.trim();
          register(label, category);
        });
      });

      (this.categories || []).forEach(category => {
        const label = `${category?.title ||
          category?.name ||
          category?.short_title ||
          ""}`.trim();
        register(label, category);
      });

      return map;
    },
    categorySetFallbackStatsByTag() {
      if (this.categorySetIsAllPages) return {};

      const statsMap = {};
      const increment = (tag, key) => {
        const normalizedTag = `${tag || ""}`.trim();
        if (!normalizedTag) return;
        if (!statsMap[normalizedTag]) {
          statsMap[normalizedTag] = { has: 0, main: 0 };
        }
        statsMap[normalizedTag][key] += 1;
      };

      this.categorySetItemsLoaded.forEach(item => {
        const additionalValues = Array.isArray(item?.additional_categories)
          ? item.additional_categories
          : [];
        const additionalTags = additionalValues
          .map(value => this.resolveCategoryTag(value))
          .map(tag => `${tag || ""}`.trim())
          .filter(Boolean);
        const additionalSet = new Set(additionalTags);

        additionalSet.forEach(tag => increment(tag, "has"));

        const mainTag = `${this.resolveCategoryTag(
          item?.category || item?.category_id
        ) || ""}`.trim();
        if (mainTag && !additionalSet.has(mainTag)) {
          increment(mainTag, "main");
        }
      });

      return statsMap;
    },
    categorySetFallbackCustomTags() {
      if (this.categorySetIsAllPages) return [];
      const knownSet = new Set(
        this.categorySetKnownTags.map(tag => this.normalizeCategoryLabel(tag))
      );
      return Object.keys(this.categorySetFallbackStatsByTag)
        .filter(tag => (this.categorySetFallbackStatsByTag[tag]?.has || 0) > 0)
        .filter(tag => !knownSet.has(this.normalizeCategoryLabel(tag)));
    },
    categorySetPendingChanges() {
      return this.categorySetPending.map((change, index) => {
        const stats = this.getCategorySetStats(change.tag);
        const addDetail = [
          `${this.formatNumberDe(stats.neu)} neu`,
          stats.has > 0 ? `${this.formatNumberDe(stats.has)} vorhanden` : ""
        ]
          .filter(Boolean)
          .join(" · ");
        return {
          id: `${change.type}:${change.tag}:${index}`,
          ...change,
          title: change.tag,
          detail:
            change.type === "add"
              ? addDetail
              : `${this.formatNumberDe(stats.has)} betroffen`
        };
      });
    },
    categorySetSummaryLabel() {
      if (!this.categorySetPendingChanges.length) {
        return "";
      }
      const addCount = this.categorySetPendingChanges.filter(
        change => change.type === "add"
      ).length;
      const removeCount = this.categorySetPendingChanges.filter(
        change => change.type === "remove"
      ).length;
      const parts = [];
      if (addCount > 0) parts.push(`${addCount} hinzufügen`);
      if (removeCount > 0) parts.push(`${removeCount} entfernen`);
      parts.push(
        `${this.formatNumberDe(this.categorySetTotal)} Veranstaltungen`
      );
      return parts.join(" · ");
    },
    categorySetCanSave() {
      return (
        this.categorySetPendingChanges.length > 0 && !this.categorySetSaving
      );
    },
    featureSetKnownTags() {
      const fromFeatures = (this.features || [])
        .map(feature =>
          `${feature?.title || feature?.name || feature?.label || ""}`.trim()
        )
        .filter(Boolean);
      const fromPrompt = (this.featuresForPrompt || [])
        .map(feature =>
          `${feature?.title || feature?.name || feature?.label || ""}`.trim()
        )
        .filter(Boolean);
      return [...new Set([...fromFeatures, ...fromPrompt])];
    },
    featureSetTagIdByNormalizedTag() {
      const map = {};
      const register = feature => {
        const label = `${feature?.title ||
          feature?.name ||
          feature?.label ||
          ""}`.trim();
        const normalizedLabel = this.normalizeCategoryLabel(label);
        const normalizedId = this.normalizeEntityId(feature);
        if (!normalizedLabel || !normalizedId || map[normalizedLabel]) return;
        map[normalizedLabel] = normalizedId;
      };

      (this.features || []).forEach(register);
      (this.featuresForPrompt || []).forEach(register);
      return map;
    },
    featureSetFallbackStatsByTag() {
      if (this.featureSetIsAllPages) return {};

      const statsMap = {};
      const increment = tag => {
        const normalizedTag = `${tag || ""}`.trim();
        if (!normalizedTag) return;
        if (!statsMap[normalizedTag]) {
          statsMap[normalizedTag] = { has: 0 };
        }
        statsMap[normalizedTag].has += 1;
      };

      this.featureSetItemsLoaded.forEach(item => {
        const featureValues = Array.isArray(item?.features)
          ? item.features
          : [];
        const featureTags = featureValues
          .map(value => this.resolveFeatureTag(value))
          .map(tag => `${tag || ""}`.trim())
          .filter(Boolean);
        [...new Set(featureTags)].forEach(tag => increment(tag));
      });

      return statsMap;
    },
    featureSetGroups() {
      const mode = this.featureSetMode;
      const query = this.normalizeCategoryLabel(this.featureSetQuery);
      const pending = this.featureSetPending;

      const sourceGroups = Array.isArray(this.featureGroups)
        ? this.featureGroups
        : [];

      if (!sourceGroups.length) {
        const fallbackEntries = this.featureSetKnownTags
          .filter(tag => this.isFeatureTagVisible(tag))
          .map(tag => {
            const stats = this.getFeatureSetStats(tag);
            return {
              id: `feature::${tag}`,
              tag,
              title: tag,
              checked: pending.some(
                pendingEntry =>
                  pendingEntry.tag === tag && pendingEntry.type === mode
              ),
              countLabel:
                mode === "remove"
                  ? `${this.formatNumberDe(
                      stats.has
                    )} von ${this.formatNumberDe(stats.total)}`
                  : ""
            };
          })
          .filter(entry => {
            if (!query) return true;
            return this.normalizeCategoryLabel(entry.title).includes(query);
          });

        if (!fallbackEntries.length) return [];
        return [
          {
            id: "feature-group",
            title: "Merkmale",
            tag: "",
            selectable: false,
            checked: false,
            countLabel: "",
            entries: fallbackEntries
          }
        ];
      }

      return sourceGroups
        .map((group, groupIndex) => {
          const groupId =
            this.normalizeEntityId(group) || `feature_group_${groupIndex}`;
          const groupTitle = `${group?.title ||
            group?.name ||
            "Merkmale"}`.trim();
          const groupFeatures = Array.isArray(group?.features)
            ? group.features
            : [];

          const dedupedTags = [
            ...new Set(
              groupFeatures
                .map(feature =>
                  `${feature?.title ||
                    feature?.name ||
                    feature?.label ||
                    ""}`.trim()
                )
                .filter(Boolean)
            )
          ];

          const entries = dedupedTags
            .filter(tag => this.isFeatureTagVisible(tag))
            .map(tag => {
              const stats = this.getFeatureSetStats(tag);
              return {
                id: `${groupId}::${tag}`,
                tag,
                title: tag,
                checked: pending.some(
                  pendingEntry =>
                    pendingEntry.tag === tag && pendingEntry.type === mode
                ),
                countLabel:
                  mode === "remove"
                    ? `${this.formatNumberDe(
                        stats.has
                      )} von ${this.formatNumberDe(stats.total)}`
                    : ""
              };
            })
            .filter(entry => {
              if (!query) return true;
              return this.normalizeCategoryLabel(entry.title).includes(query);
            });

          if (!entries.length) return null;

          return {
            id: groupId,
            title: groupTitle,
            tag: "",
            selectable: false,
            checked: false,
            countLabel: "",
            entries
          };
        })
        .filter(Boolean);
    },
    featureSetPendingChanges() {
      return this.featureSetPending.map((change, index) => {
        const stats = this.getFeatureSetStats(change.tag);
        const addDetail = [
          `${this.formatNumberDe(stats.neu)} neu`,
          stats.has > 0 ? `${this.formatNumberDe(stats.has)} vorhanden` : ""
        ]
          .filter(Boolean)
          .join(" · ");
        return {
          id: `${change.type}:${change.tag}:${index}`,
          ...change,
          title: change.tag,
          detail:
            change.type === "add"
              ? addDetail
              : `${this.formatNumberDe(stats.has)} betroffen`
        };
      });
    },
    featureSetSummaryLabel() {
      if (!this.featureSetPendingChanges.length) {
        return "";
      }
      const addCount = this.featureSetPendingChanges.filter(
        change => change.type === "add"
      ).length;
      const removeCount = this.featureSetPendingChanges.filter(
        change => change.type === "remove"
      ).length;
      const parts = [];
      if (addCount > 0) parts.push(`${addCount} hinzufügen`);
      if (removeCount > 0) parts.push(`${removeCount} entfernen`);
      parts.push(
        `${this.formatNumberDe(this.featureSetTotal)} Veranstaltungen`
      );
      return parts.join(" · ");
    },
    featureSetCanSave() {
      return this.featureSetPendingChanges.length > 0 && !this.featureSetSaving;
    }
  },
  beforeDestroy() {
    this.clearCategorySetJobTimer();
  },
  watch: {
    filter: {
      deep: true,
      handler() {
        this.clearSelection();
      }
    },
    selected(newSelection) {
      if (this.allPagesSelected && newSelection.length !== this.eventCount) {
        this.allPagesSelected = false;
      }
    }
  }
};
